/**
 * StructureSidebarBasicInfo - Router Component
 * 
 * This component acts as a router that manages shared logic and delegates
 * rendering to specialized components based on item type:
 * 
 * - BasicInfoObject: For glossary objects/items
 * - BasicInfoTemplate: For blueprints/templates
 * - BasicInfoDialog: For dialog systems
 * - BasicInfoDocument: For documents
 * 
 * Each specialized component in ./basic-info/ handles its own unique fields
 * and UI, making it easier to maintain and customize per type.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { useDialogDataContext } from "@/providers/DialogDataProvider";
import { useDocumentDataContext } from "@/providers/DocumentDataProvider";
import { useDialogSectionOptional } from '@/contexts/DialogSectionContext';
import { objectsStore } from '@/lib/stores/objectsStore';
import { templatesStore } from '@/lib/stores/templatesStore';
import { documentStore, updateDocumentItem } from '@/lib/stores/documentStore';
import { useToast } from '@/hooks/use-toast';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { isBasicInfoVisibleForRole } from '@/lib/utils/blueprintConstants';
import { getCompanyRoles, getUserRole } from '@/lib/utils/permissions';
import { useDevRoleOverride } from '@/lib/utils/devRoleOverride';
import { apiService } from '@/lib/api-functions/api-service';
import { fetchStatuses, StatusWorkflow } from '@/lib/stores/statusStore';
import { 
  ItemType, 
  BasicInfoData,
  BasicInfoObject,
  BasicInfoTemplate,
  BasicInfoDialog,
  BasicInfoDocument,
} from './basic-info';

export default function StructureSidebarBasicInfo() {
  const { t } = usePlatformTranslation();
  const params = useParams();
  const location = useLocation();
  const { toast } = useToast();
  const { selectedGame, selectedCompany, currentUser } = useSimplifiedUserData();
  const { overrideRoleId } = useDevRoleOverride();
  const dialogSectionContext = useDialogSectionOptional();

  // Get templates, objects, dialogs, and documents context
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { glossaryItems } = useGlossaryDataContext();
  const { dialogs } = useDialogDataContext();
  const { documents } = useDocumentDataContext();

  // State
  const [itemData, setItemData] = useState<BasicInfoData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedData, setEditedData] = useState<BasicInfoData | null>(null);
  const [availableStatuses, setAvailableStatuses] = useState<StatusWorkflow>({});

  // Determine current context and item
  const currentContext = useMemo(() => {
    const path = location.pathname;
    
    if (path.startsWith('/library/') && params.itemId) {
      return {
        itemType: 'object' as ItemType,
        itemId: params.itemId,
      };
    }
    
    if (path.startsWith('/templates/') && params.itemId) {
      return {
        itemType: 'template' as ItemType,
        itemId: params.itemId,
      };
    }
    
    if (path.startsWith('/documents/') && params.documentId) {
      return {
        itemType: 'document' as ItemType,
        itemId: params.documentId,
      };
    }
    
    if (path.startsWith('/dialogs/') && params.dialogId) {
      return {
        itemType: 'dialog' as ItemType,
        itemId: params.dialogId,
      };
    }
    
    return null;
  }, [location.pathname, params]);

  // Get current item from store OR from dialog context (for new unsaved dialogs)
  const currentItem = useMemo(() => {
    if (!currentContext) return null;

    if (currentContext.itemType === 'object') {
      return glossaryItems.find(item => item.id === currentContext.itemId);
    }
    
    if (currentContext.itemType === 'template') {
      return glossaryBlueprints.find(bp => bp.id === currentContext.itemId);
    }

    if (currentContext.itemType === 'dialog') {
      // PRIORITY: If dialog wizard is open (context metadata available), use that as source of truth
      // This ensures the sidebar reflects live edits in the wizard, not stale stored data
      if (dialogSectionContext?.dialogMetadata) {
        const metadata = dialogSectionContext.dialogMetadata;
        // Create a temporary item object from context metadata
        return {
          id: metadata.dialogId || 'new',
          name: metadata.dialogName,
          dialog_key: metadata.dialogKey,
          description: metadata.description,
          tags: metadata.tags,
          status: metadata.status,
          owner: metadata.owner,
          edit_permissions: metadata.editPermissions,
          data: {
            sections: metadata.sections,
            startingSection: metadata.startingSection,
            translatable: metadata.translatable,
            speakerMode: metadata.speakerMode,
            speakerTypeId: metadata.speakerTypeId,
            speakerTemplateIds: metadata.speakerTemplateIds,
            variables: metadata.dialogVariables,
          },
        } as any;
      }
      
      // Fallback to store if dialog wizard is not open
      const dialogFromStore = dialogs.find(dialog => dialog.id === currentContext.itemId);
      return dialogFromStore;
    }

    if (currentContext.itemType === 'document') {
      return documents.find(doc => doc.id === currentContext.itemId);
    }

    return null;
  }, [currentContext, glossaryItems, glossaryBlueprints, dialogs, documents, dialogSectionContext?.dialogMetadata]);

  // Get the effective user's role for this company (includes dev override)
  const currentUserRole = useMemo(() => {
    if (!selectedGame?.company_id) return null;
    
    const roleName = getUserRole(selectedGame.company_id);
    if (!roleName) return null;
    
    const companyRoles = getCompanyRoles(selectedGame.company_id);
    const role = companyRoles.find(r => r.role_name.toLowerCase() === roleName.toLowerCase());
    
    return role?.id || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame?.company_id]);

  // Check visibility and edit permissions
  const permissions = useMemo(() => {
    if (!currentItem || !currentUserRole) {
      return { isVisible: true, canEdit: true }; // Default to visible and editable if no data
    }

    // Check basic info visibility for role
    const isVisible = isBasicInfoVisibleForRole(currentItem, currentUserRole);
    if (!isVisible) {
      return { isVisible: false, canEdit: false };
    }

    // Check if user can edit based on owner and permissions
    let canEdit = true;
    
    // If there's an owner set
    if (currentItem.owner) {
      // If user is owner, can edit
      if (currentItem.owner === currentUser?.id) {
        canEdit = true;
      } 
      // If locked and user is not owner, cannot edit
      else if (currentItem.edit_permissions === 'Locked') {
        canEdit = false;
      }
      // If open, anyone can edit
      else {
        canEdit = true;
      }
    }

    return { isVisible, canEdit };
  }, [currentItem, currentUserRole, currentUser?.id]);

  // Fetch available statuses for the company/game
  useEffect(() => {
    const loadStatuses = async () => {
      if (!selectedCompany) return;
      
      try {
        const statuses = await fetchStatuses(
          selectedCompany.id,
          selectedGame?.id
        );

        // Find game-specific or company default
        let config = null;
        
        if (selectedGame) {
          // Look for game-specific config
          config = statuses.find(s => s.game_id === selectedGame.id) || null;
          
          // If no game-specific, use company default
          if (!config) {
            config = statuses.find(s => s.game_id === null) || null;
          }
        } else {
          // No game selected, use company default
          config = statuses.find(s => s.game_id === null) || null;
        }

        if (config) {
          // Determine which workflow to use based on item type
          let workflow: StatusWorkflow = {};
          
          if (currentContext?.itemType === 'template') {
            // Templates use the templates workflow
            workflow = config.templates || {};
          } else if (currentContext?.itemType === 'object') {
            // Objects use the objects workflow
            workflow = config.objects || {};
          } else if (currentContext?.itemType === 'dialog') {
            // Dialogs use the dialogs workflow (or objects as fallback)
            workflow = config.dialogs || config.objects || {};
          } else if (currentContext?.itemType === 'document') {
            // Documents use the objects workflow as fallback
            workflow = config.objects || {};
          }
          
          setAvailableStatuses(workflow);
        } else {
          // Fallback to draft/published if no config
          setAvailableStatuses({
            draft: { color: "#d3d933" },
            published: { color: "#6e35ff" }
          });
        }
      } catch (error) {
        console.error('Error loading statuses:', error);
        // Fallback to draft/published on error
        setAvailableStatuses({
          draft: { color: "#d3d933" },
          published: { color: "#6e35ff" }
        });
      }
    };

    loadStatuses();
  }, [selectedCompany, selectedGame, currentContext?.itemType]);

  // Load item data when current item changes
  useEffect(() => {
    if (!currentItem) {
      setItemData(null);
      setEditedData(null);
      setIsEditing(false);
      return;
    }

    // CRITICAL FIX: Don't overwrite editedData while user is actively editing
    // This prevents the infinite loop where context updates constantly reset user changes
    if (isEditing) {
      return;
    }

    const data: BasicInfoData = {
      id: currentItem.id,
      description: currentItem.description || '',
      tags: currentItem.tags || [],
      identifiers: (currentItem as any).identifiers || [],
      code_id: (currentItem as any).code_id || '',
      owner: currentItem.owner || null,
      edit_permissions: currentItem.edit_permissions || 'Open',
      status: (currentItem as any).status || Object.keys(availableStatuses)[0] || 'published',
    };

    // Add color for templates
    if (currentContext?.itemType === 'template') {
      data.color = (currentItem as any).settings?.color || '#000000';
    }

    // Add dialog-specific fields
    if (currentContext?.itemType === 'dialog') {
      data.startingSection = (currentItem as any).data?.startingSection;
      data.translatable = (currentItem as any).data?.translatable !== false;
      data.speakerMode = (currentItem as any).data?.speakerMode || 'none';
      data.speakerTypeId = (currentItem as any).data?.speakerTypeId || null;
      data.speakerTemplateIds = (currentItem as any).data?.speakerTemplateIds || [];
      data.dialogVariables = (currentItem as any).data?.variables || [];
    }

    // Documents don't currently have description, owner, or edit_permissions in database
    // But we still load tags and status if available
    if (currentContext?.itemType === 'document') {
      // Documents have a simpler structure
      data.description = ''; // Not yet supported
      data.owner = null; // Not yet supported
      data.edit_permissions = 'Open'; // Not yet supported
    }

    setItemData(data);
    setEditedData(data);
  }, [currentItem, currentContext, availableStatuses, isEditing]);

  // Handle save
  const handleSave = async () => {
    if (!editedData || !currentItem || !selectedGame?.id) return;

    setIsSaving(true);
    try {
      // CRITICAL FIX: For dialogs, prevent database save from sidebar
      // The sidebar is rendered outside DialogSectionProvider, so we don't have context access
      // Instead, we only allow saving through the dialog wizard itself
      if (currentContext?.itemType === 'dialog') {
        // If we have dialog context with metadata update callback, use it
        if (dialogSectionContext?.onMetadataUpdate) {
          // Update metadata in the wizard locally
          dialogSectionContext.onMetadataUpdate({
            description: editedData.description,
            tags: editedData.tags,
            status: editedData.status,
            owner: editedData.owner,
            edit_permissions: editedData.edit_permissions,
            startingSection: editedData.startingSection,
            translatable: editedData.translatable,
            speakerMode: editedData.speakerMode,
            speakerTypeId: editedData.speakerTypeId,
            speakerTemplateIds: editedData.speakerTemplateIds,
            dialogVariables: editedData.dialogVariables,
          });

          // Update local state
          setItemData(editedData);
          setIsEditing(false);

          toast({
            title: t('platform.common.success'),
            description: 'Basic information updated. Click "Update Dialog" to save all changes.',
          });
        } else {
          // No dialog context - dialog is open in wizard but sidebar can't communicate with it
          // Don't save to database as it will cause a reload and wipe edits
          // Instead, just update local state and inform user
          setItemData(editedData);
          setIsEditing(false);

          toast({
            title: t('platform.common.info'),
            description: 'Changes noted. Use the "Update Dialog" button in the dialog editor to save.',
          });
        }

        setIsSaving(false);
        return;
      }
      if (currentContext?.itemType === 'object') {
        // Update object with skipVersionBump flag using apiService
        const result = await apiService.updateObject({
          id: currentItem.id,
          game_id: selectedGame.id,
          name: (currentItem as any).name,
          type: (currentItem as any).type || 'glossary',
          data: (currentItem as any).data,
          tags: editedData.tags,
          blueprint_id: (currentItem as any).blueprint_id,
          identifiers: editedData.identifiers,
          code_id: editedData.code_id,
          version: (currentItem as any).version,
          folder: (currentItem as any).folder,
          owner: editedData.owner,
          edit_permissions: editedData.edit_permissions,
          description: editedData.description,
          status: editedData.status,
          ...({ skipVersionBump: true } as any), // Don't increment version for basic info changes
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to update object');
        }

        // Update local store
        const currentData = objectsStore.getData();
        const updatedData = currentData.map(item => 
          item.id === currentItem.id ? result.data : item
        );
        objectsStore.setData(updatedData);

        toast({
          title: t('platform.common.success'),
          description: 'Basic information updated successfully',
        });
      } else if (currentContext?.itemType === 'template') {
        // Update template with skipVersionBump flag using apiService
        const result = await apiService.updateBlueprint({
          id: currentItem.id,
          game_id: selectedGame.id,
          name: (currentItem as any).name,
          tags: editedData.tags,
          type: (currentItem as any).type,
          description: editedData.description,
          data: (currentItem as any).data,
          settings: {
            ...(currentItem as any).settings,
            color: editedData.color,
          },
          version: (currentItem as any).version,
          folder: (currentItem as any).folder,
          owner: editedData.owner,
          edit_permissions: editedData.edit_permissions,
          status: editedData.status,
          ...({ skipVersionBump: true } as any), // Don't increment version for basic info changes
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to update template');
        }

        // Update local store
        const currentData = templatesStore.getData();
        const updatedData = currentData.map(item => 
          item.id === currentItem.id ? result.data : item
        );
        templatesStore.setData(updatedData);

        toast({
          title: t('platform.common.success'),
          description: 'Basic information updated successfully',
        });
      } else if (currentContext?.itemType === 'document') {
        // Update document - currently only tags and status are supported
        const result = await updateDocumentItem({
          id: currentItem.id,
          game_id: selectedGame.id,
          name: (currentItem as any).name,
          data: (currentItem as any).data || [],
          tags: editedData.tags,
          status: (editedData.status || 'published') as 'draft' | 'published',
          folder: (currentItem as any).folder,
          settings: (currentItem as any).settings,
        }, toast);

        if (!result) {
          throw new Error('Failed to update document');
        }

        // Update local store
        const currentData = documentStore.getData();
        const updatedData = currentData.map(item => 
          item.id === currentItem.id ? result : item
        );
        documentStore.setData(updatedData);

        toast({
          title: t('platform.common.success'),
          description: 'Document information updated successfully',
        });
      }
      // NOTE: Dialog save path removed - dialogs should only be saved through the wizard
      // This prevents database updates from triggering reloads that wipe unsaved wizard edits

      // Reset editing state
      setItemData(editedData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save basic info:', error);
      toast({
        title: t('platform.common.error'),
        description: error instanceof Error ? error.message : 'Failed to save basic information',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setEditedData(itemData);
    setIsEditing(false);
  };

  // Don't render if not visible for the user's role
  if (!permissions.isVisible) {
    return null;
  }

  // Show empty state if no item selected
  // BUT: If we're in a dialog context, wait for metadata to be available (race condition on mount)
  if (!currentContext || !currentItem || !itemData) {
    // Special case: if we're viewing a dialog and have dialogSectionContext but no metadata yet,
    // show loading state instead of empty state (metadata will arrive shortly)
    if (currentContext?.itemType === 'dialog' && dialogSectionContext && !dialogSectionContext.dialogMetadata) {
      return (
        <div className="p-4 text-center text-muted-foreground">
          <p>Loading...</p>
        </div>
      );
    }
    
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>{t('platform.layout.structure-menu.select_an_item_to_view_basic_information')}</p>
      </div>
    );
  }

  // Common props for all specialized components
  const commonProps = {
    itemData,
    editedData: editedData!,
    setEditedData,
    isEditing,
    setIsEditing,
    isSaving,
    availableStatuses,
    permissions,
    currentItem,
    selectedGame,
    onSave: handleSave,
    onCancel: handleCancel,
  };

  // Route to the appropriate specialized component based on item type
  switch (currentContext.itemType) {
    case 'object':
      return <BasicInfoObject {...commonProps} />;
    
    case 'template':
      return <BasicInfoTemplate {...commonProps} />;
    
    case 'dialog':
      return <BasicInfoDialog {...commonProps} />;
    
    case 'document':
      return <BasicInfoDocument {...commonProps} />;
    
    default:
      return (
        <div className="p-4 text-center text-muted-foreground">
          <p>Unknown item type</p>
        </div>
      );
  }
}
